/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext, AuthContextUser, AuthContextValues, defaultAuthValue } from './auth';
import { executeAuthMethod, getCookieAuth } from 'utils/auth';
import {
  loginMasquerade,
  loginHost,
  logoutHost,
  createAnonymousSession,
  getCurrentOwner,
  getCurrentGuest,
} from 'api/auth';
import { Role } from 'api/types/auth';
import { Spin } from 'ui-kit';
import { recordLeadForLeadDyno } from 'components/LeadDynoWidget';
import { LEADDYNO_RELEASE_DATE, TOLT_RELEASE_DATE } from 'consts';
import { recordLeadForTolt } from 'utils/integrations';

const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState<AuthContextValues>(defaultAuthValue);

  const hostLogout = useCallback(() => {
    logoutHost().then(() => {
      setAuth(defaultAuthValue);
    });
  }, []);

  const hostLogin = useCallback((email, password) => {
    return executeAuthMethod({ role: Role.HOST }, () => loginHost(email, password)).then(setAuth);
  }, []);

  const guestLogin = useCallback(() => {
    return executeAuthMethod({ role: Role.GUEST_ANONYMOUS }, createAnonymousSession).then(setAuth);
  }, []);

  const masqueradeLogin = useCallback(() => {
    return executeAuthMethod({ role: Role.HOST, isMasquerading: true }, loginMasquerade);
  }, []);

  const handleAuthMethod = useCallback((methodPromise: Promise<AuthContextUser>) => {
    methodPromise
      .then((user) => {
        setAuth(user);

        // When the user is created before the LeadDyno release date, we don't want to record the lead
        // Here we want to record the lead for the user when they are logged in via social login
        if (new Date(user.dateCreated) > LEADDYNO_RELEASE_DATE) {
          recordLeadForLeadDyno({
            email: user.email,
            firstName: user.name,
          });
        }

        // When the user is created before the Tolt.io release date, we don't want to record the lead
        // Here we want to record the lead for the user when they are logged in via social login
        if (new Date(user.dateCreated) > TOLT_RELEASE_DATE) {
          recordLeadForTolt({
            email: user.email,
          });
        }

        if (user?.hasSetPassword) {
          window.fbq?.(
            'trackCustom',
            'CreateAccount',
            { from: 'social', email: user?.email },
            { eventID: user?.id },
          );
        }
      })
      .catch(() => setAuth(defaultAuthValue))
      .finally(() => setLoading(false));
  }, []);

  const increseStores = useCallback((count) => {
    setAuth((prev) => ({ ...prev, stores: prev.stores + count }));
  }, []);

  useEffect(() => {
    const { role: initialRole } = getCookieAuth();

    switch (initialRole) {
      case Role.HOST:
        handleAuthMethod(executeAuthMethod({ role: Role.HOST }, getCurrentOwner));
        break;
      case Role.GUEST_ANONYMOUS:
        handleAuthMethod(executeAuthMethod({ role: Role.GUEST_ANONYMOUS }, getCurrentGuest));
        break;
      case Role.ADMIN:
        handleAuthMethod(masqueradeLogin());
        break;
      default:
        setAuth(defaultAuthValue);
        setLoading(false);
    }
  }, []);

  return (
    <Spin spinning={loading}>
      <AuthContext.Provider value={{ ...auth, increseStores, hostLogin, hostLogout, guestLogin }}>
        {!loading && children}
      </AuthContext.Provider>
    </Spin>
  );
};

export default AuthProvider;
